import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ToastProvider } from './context/ToastContext';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import DeleteAccount from './pages/DeleteAccount';
import Cafes from './pages/Cafes';
import CafeDetail from './pages/CafeDetail';
import Cart from './pages/Cart';
import MpgsPayPage from './pages/MpgsPayPage';
import Orders from './pages/Orders';
import Profile from './pages/Profile';
import RouteMap from './pages/RouteMap';
import BaristaDashboard from './pages/BaristaDashboard';
import BaristaLogin from './pages/BaristaLogin';
import QRScanner from './pages/QRScanner';
import HelpCenter from './pages/HelpCenter';
import OrderDetails from './pages/OrderDetails';
import Settings from './pages/Settings';
import Promotions from './pages/Promotions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Navbar from './components/Navbar';
import Notifications from './components/Notifications';
import { NotificationProvider } from './context/NotificationContext';
import BottomNavigation from './components/BottomNavigation';
import { ErrorBoundary } from './components/ErrorBoundary';
import axios from './config/api';
import { useOffline } from './hooks/useOffline';
import { pushNotificationService } from './services/pushNotificationService';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { useToast } from './context/ToastContext';
import { consumeHardwareBack } from './utils/hardwareBackBridge';
import { getBaristaTokenSync, getCustomerTokenSync } from './utils/authStorage';

const EXIT_CONFIRM_MS = 2500;

/** Main tab routes: double-back exits the app instead of WebView history. */
const ROOT_TAB_PATHS = new Set([
  '/',
  '/cafes',
  '/route',
  '/cart',
  '/orders',
  '/profile',
  '/settings',
  '/promotions',
  '/help',
]);

function normalizePath(path: string): string {
  const base = path.split('?')[0].split('#')[0];
  if (base.length > 1 && base.endsWith('/')) {
    return base.slice(0, -1);
  }
  return base || '/';
}

function isRootTabPath(path: string): boolean {
  return ROOT_TAB_PATHS.has(normalizePath(path));
}

/** Staff console paths — no consumer chrome (navbar, bottom nav, notifications strip). */
export function isStaffConsolePath(pathname: string): boolean {
  return pathname === '/barista' || pathname.startsWith('/barista/');
}

export function isCardPayPath(pathname: string): boolean {
  return pathname === '/pay' || pathname.startsWith('/pay/');
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return user ? (
    <>{children}</>
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  );
}

/** Staff dashboard requires a barista/superuser session, not a customer login. */
function StaffPrivateRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const hasBarista =
    typeof window !== 'undefined' && !!getBaristaTokenSync();

  if (!hasBarista) {
    return <Navigate to="/barista/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/barista/login" element={<BaristaLogin />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route
        path="/cafes"
        element={
          <PrivateRoute>
            <Cafes />
          </PrivateRoute>
        }
      />
      <Route
        path="/cafe/:id"
        element={
          <PrivateRoute>
            <CafeDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/cart"
        element={
          <PrivateRoute>
            <Cart />
          </PrivateRoute>
        }
      />
      <Route
        path="/pay"
        element={
          <PrivateRoute>
            <MpgsPayPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <PrivateRoute>
            <Orders />
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute>
            <Profile />
          </PrivateRoute>
        }
      />
      <Route
        path="/route"
        element={
          <PrivateRoute>
            <RouteMap />
          </PrivateRoute>
        }
      />
      <Route
        path="/barista"
        element={
          <StaffPrivateRoute>
            <BaristaDashboard />
          </StaffPrivateRoute>
        }
      />
      <Route
        path="/qr-scanner"
        element={
          <PrivateRoute>
            <QRScanner />
          </PrivateRoute>
        }
      />
      <Route
        path="/help"
        element={
          <PrivateRoute>
            <HelpCenter />
          </PrivateRoute>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <PrivateRoute>
            <OrderDetails />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/promotions"
        element={
          <PrivateRoute>
            <Promotions />
          </PrivateRoute>
        }
      />
      <Route
        path="/privacy"
        element={
          <PrivateRoute>
            <PrivacyPolicy />
          </PrivateRoute>
        }
      />
      <Route
        path="/terms"
        element={
          <PrivateRoute>
            <TermsOfService />
          </PrivateRoute>
        }
      />
      <Route path="/" element={<Navigate to="/cafes" />} />
    </Routes>
  );
}

function BackButtonHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const pathnameRef = React.useRef(location.pathname);
  const lastBackRef = React.useRef(0);
  const showToastRef = React.useRef(showToast);
  const navigateRef = React.useRef(navigate);

  React.useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  React.useEffect(() => {
    showToastRef.current = showToast;
    navigateRef.current = navigate;
  }, [showToast, navigate]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let removed = false;
    let listenerHandle: { remove: () => Promise<void> } | undefined;

    void CapacitorApp.addListener('backButton', (event: { canGoBack: boolean }) => {
      if (consumeHardwareBack()) {
        return;
      }

      const { canGoBack } = event;
      const path = normalizePath(pathnameRef.current);

      if (isStaffConsolePath(path)) {
        if (canGoBack) {
          navigateRef.current(-1);
        } else {
          void CapacitorApp.exitApp();
        }
        return;
      }

      if (!isRootTabPath(path)) {
        if (canGoBack) {
          navigateRef.current(-1);
        } else {
          navigateRef.current('/cafes', { replace: true });
        }
        return;
      }

      const now = Date.now();
      if (now - lastBackRef.current < EXIT_CONFIRM_MS) {
        void CapacitorApp.exitApp();
        return;
      }

      lastBackRef.current = now;
      showToastRef.current('Press back again to exit Qahwahub', 'info');
    }).then((handle: { remove: () => Promise<void> }) => {
      if (removed) {
        void handle.remove();
      } else {
        listenerHandle = handle;
      }
    });

    return () => {
      removed = true;
      if (listenerHandle) {
        void listenerHandle.remove();
      }
    };
  }, []);

  return null;
}

function WebsiteLinkHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let removed = false;
    let listenerHandle: { remove: () => Promise<void> } | undefined;
    const openUrl = (url?: string | null) => {
      if (!url) return;
      try {
        const parsed = new URL(url);
        const host = parsed.hostname.replace(/^www\./, '');
        if (parsed.protocol !== 'https:' || host !== 'qahwahub.com') return;
        const path = `${parsed.pathname || '/'}${parsed.search || ''}`;
        if (!path.startsWith('/') || path.startsWith('//')) return;
        navigate(path, { replace: true });
      } catch {
        /* ignore malformed links */
      }
    };
    void CapacitorApp.getLaunchUrl().then((result: { url: string } | undefined) => {
      if (!removed) openUrl(result?.url);
    });
    void CapacitorApp.addListener('appUrlOpen', (event: { url: string }) => {
      openUrl(event.url);
    }).then((handle: { remove: () => Promise<void> }) => {
      if (removed) void handle.remove();
      else listenerHandle = handle;
    });
    return () => {
      removed = true;
      if (listenerHandle) void listenerHandle.remove();
    };
  }, [navigate]);
  return null;
}

function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  const staffConsole = isStaffConsolePath(location.pathname);
  const cardPay = isCardPayPath(location.pathname);
  const hideConsumerChrome = staffConsole || cardPay;

  useOffline();

  useEffect(() => {
    if (staffConsole) {
      document.documentElement.classList.remove('dark');
      document.title = 'QahwaHub Console — Staff';
      return;
    }
    if (cardPay) {
      document.title = 'QahwaHub — Secure payment';
      return;
    }
    document.title = 'QahwaHub — Order & earn rewards';

    const checkDarkMode = async () => {
      try {
        const token = getCustomerTokenSync();
        if (token) {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Settings request timeout')), 5000)
          );

          const response = (await Promise.race([
            axios.get('/api/settings'),
            timeoutPromise,
          ])) as any;

          if (response.data.dark_mode) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }
      } catch (error) {
        console.log('Could not load dark mode setting:', error);
      }
    };

    checkDarkMode();
  }, [staffConsole, cardPay]);

  useEffect(() => {
    if (user && !user.isGuest && !staffConsole && !cardPay) {
      pushNotificationService.initialize().catch((error) => {
        console.error('Failed to initialize push notifications:', error);
      });
    }

    return () => {
      if (!user) {
        pushNotificationService.cleanup();
      }
    };
  }, [user, staffConsole, cardPay]);

  return (
    <div
      className={
        staffConsole
          ? 'min-h-screen overflow-y-auto bg-[#ebe3d6]'
          : cardPay
            ? 'min-h-screen bg-[#fff8f0]'
            : 'min-h-screen bg-[#fbf4ec] dark:bg-transparent'
      }
      style={{
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
      }}
    >
      <BackButtonHandler />
      <WebsiteLinkHandler />
      {!hideConsumerChrome && <Navbar />}
      {!hideConsumerChrome && <Notifications />}
      <div className={hideConsumerChrome ? undefined : 'consumer-page-bottom-pad'}>
        <ErrorBoundary>
          <AppRoutes />
        </ErrorBoundary>
      </div>
      {!hideConsumerChrome && <BottomNavigation />}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <ToastProvider>
            <Router>
              <NotificationProvider>
                <AppShell />
              </NotificationProvider>
            </Router>
          </ToastProvider>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
